import type {
  DraftMessageId,
  SupportTicketId,
} from "@redotech/effect-api-shared/ids/definitions";
import {
  DraftMessageId as DraftMessageIdSchema,
  SupportTicketId as SupportTicketIdSchema,
} from "@redotech/effect-api-shared/ids/definitions";
import type { SaveDraftInput } from "@redotech/effect-api-shared/models/shopper-ai/conversation/draft";
import type { SupportTicketDraftResponse } from "@redotech/effect-api-shared/rpc/support-ticket";
import { BasicIndex } from "@tanstack/db";
import { createCollection } from "@tanstack/react-db";
import type { QueryClient } from "@tanstack/react-query";
import { Option } from "effect";
import { requireSupportTicketIdScope } from "../query/support-ticket-collection-scope";
import { supportTicketQueryCollectionOptions } from "../query/support-ticket-query-collection-options";
import { supportTicketDraftQueryKeys } from "../query/support-ticket-query-keys";
import { parseCollectionFilters } from "../query/support-ticket-tanstack-scope";
import {
  buildSaveDraftPayload,
  type DraftCollectionItem,
} from "./support-ticket-draft-requests";

export interface SupportTicketDraftsApi {
  getDrafts(
    supportTicketId: SupportTicketId,
  ): Promise<{ drafts: readonly SupportTicketDraftResponse[] }>;
  saveDraft(
    supportTicketId: SupportTicketId,
    payload: SaveDraftInput,
  ): Promise<void>;
  deleteDraft(
    supportTicketId: SupportTicketId,
    draftId: DraftMessageId,
  ): Promise<void>;
}

export interface CreateSupportTicketDraftsCollectionOptions {
  queryClient: QueryClient;
  api: SupportTicketDraftsApi;
}

// A long staleTime stops ambient refetches from reverting the optimistic
// `status: "SENDING"` mid-send (which would break the messageCreated cleanup and
// the hasSendingDraft gate). Drafts stay current via SSE + the onInsert/onUpdate
// refetch. Exported so the per-ticket live query sets a matching gcTime: the
// on-demand subset must be retained at least as long as its cache is fresh, or
// switching tickets GCs the subset and the stale-empty cache suppresses the
// refetch on return.
export const DRAFTS_COLLECTION_STALE_TIME_MS = 5 * 60 * 1000;

export function buildDraftsQueryCollectionOptions({
  queryClient,
  api,
}: CreateSupportTicketDraftsCollectionOptions) {
  return supportTicketQueryCollectionOptions({
    queryKey: supportTicketDraftQueryKeys.all,
    queryFn: async (ctx): Promise<DraftCollectionItem[]> => {
      const supportTicketId = requireSupportTicketIdScope(
        parseCollectionFilters(ctx.meta?.loadSubsetOptions),
      );
      const result = await api.getDrafts(supportTicketId);
      return result.drafts.map((d) => ({
        ...d,
        inReplyToMessageId: Option.getOrUndefined(
          d.inReplyToMessageId,
        )?.toString(),
      }));
    },
    queryClient,
    getKey: (item) => item.id.toString(),
    syncMode: "on-demand",
    autoIndex: "eager",
    defaultIndexType: BasicIndex,
    staleTime: DRAFTS_COLLECTION_STALE_TIME_MS,
    meta: { showErrorToast: true },
    recoverTransientFailures: false,
    onInsert: async ({ transaction }) => {
      for (const mutation of transaction.mutations) {
        const item = mutation.modified;
        await api.saveDraft(
          SupportTicketIdSchema.make(item.supportTicketId),
          buildSaveDraftPayload(item),
        );
      }
      await transaction.mutations[0].collection.utils.refetch();
    },
    onUpdate: async ({ transaction }) => {
      for (const mutation of transaction.mutations) {
        const item = mutation.modified;
        await api.saveDraft(
          SupportTicketIdSchema.make(item.supportTicketId),
          buildSaveDraftPayload(item),
        );
      }
      // Sync the persisted edit back into the collection's confirmed state.
      // TanStack DB drops the optimistic mutation once this handler resolves,
      // so skipping the refetch (the previous `{ refetch: false }`) left the
      // synced cache holding the pre-edit content. The composer still showed
      // the new text (the editor keeps its own state, gated by
      // hasLoadedDraft), so the save *looked* applied — but navigating away
      // and back reloaded the stale cached draft. Refetch like onInsert does.
      await transaction.mutations[0].collection.utils.refetch();
    },
    onDelete: async ({ transaction }) => {
      for (const mutation of transaction.mutations) {
        const item = mutation.original;
        await api.deleteDraft(
          SupportTicketIdSchema.make(item.supportTicketId),
          DraftMessageIdSchema.make(String(mutation.key)),
        );
      }
    },
  });
}

export function createDraftsCollection(
  options: CreateSupportTicketDraftsCollectionOptions,
) {
  return createCollection<DraftCollectionItem, string>(
    buildDraftsQueryCollectionOptions(options),
  );
}

export type DraftsCollection = ReturnType<typeof createDraftsCollection>;
